import { Button, DialogTrigger } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AttributeResponse, useAttributes } from '@/entities/attributes';
import { useDebouncedSearch } from '@/shared/hooks';
import { getOutsideTotal } from '@/shared/lib';
import { SelectionDialog, useLocalSelectionItems, useSelectionDialogState } from '@/shared/ui';

/** Сколько атрибутов показывать в окне: список набирается поиском по всему набору, а не листается */
const ATTRIBUTES_PAGE_LIMIT = 50;

const getAttributeCode = (attribute: AttributeResponse) => attribute.code;
const getAttributeName = (attribute: AttributeResponse) => attribute.name;
const getAttributeDescription = (attribute: AttributeResponse) => attribute.description;

type Props = {
  /** Коды атрибутов, уже входящих в группу */
  existingAttributeCodes: string[];
  onSave: (attributes: AttributeResponse[]) => void;
};

/** Кнопка «Добавить атрибут» и окно выбора атрибутов для группы */
export const AddAttributeDialog: FC<Props> = ({ existingAttributeCodes, onSave }) => {
  const { t } = useTranslation(['common', 'attributeGroups']);

  const { isOpen, search, setSearch, selected, handleOpenChange, handleToggle } = useSelectionDialogState({
    getItemId: getAttributeCode,
  });
  const appliedSearch = useDebouncedSearch(search);

  // Поиск идёт по всему набору атрибутов: атрибут за пределами первой порции иначе недостижим
  const { data, isLoading } = useAttributes({
    search: appliedSearch || undefined,
    limit: ATTRIBUTES_PAGE_LIMIT,
    'is-deleted': false,
  });
  const loadedAttributes = data?.attributes ?? [];

  const items = useLocalSelectionItems({
    items: loadedAttributes,
    getItemId: getAttributeCode,
    excludedIds: existingAttributeCodes,
  });

  const handleConfirm = () => {
    onSave(selected);
    handleOpenChange(false);
  };

  return (
    <SelectionDialog<AttributeResponse>
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={
        <DialogTrigger asChild>
          <Button variant="secondary" size="sm" className="ml-auto">
            {t('attributeGroups:addAttribute')}
          </Button>
        </DialogTrigger>
      }
      title={t('attributeGroups:addAttribute')}
      search={search}
      onSearchChange={setSearch}
      items={items}
      // Атрибуты группы за выдачей окну неизвестны: из общего числа вычитаются только попавшие в неё
      total={getOutsideTotal(data?.total, loadedAttributes.length, loadedAttributes.length - items.length)}
      isLoading={isLoading}
      selected={selected}
      onToggle={handleToggle}
      getItemId={getAttributeCode}
      renderTitle={getAttributeName}
      renderHint={getAttributeCode}
      renderDescription={getAttributeDescription}
      labels={{
        empty: t('common:noItemsToSelect'),
        notFound: t('attributeGroups:noAttributesFound'),
        confirm: t('common:add'),
      }}
      onConfirm={handleConfirm}
    />
  );
};
